/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';

import { Button, Icon } from '@mairistem/composants';
import { Liste, ListeQueryContext } from '@mairistem/liste';

import COLORS from '@/constants/colors';
import type { CleMentionActe, MentionActe } from '@/types';

import MentionListeTable from './MentionListeTable';
import ModalAjouterMention from './ModalAjouterMention';
import ModalModifierMention from './ModalModifierMention';
import ModalSupprimerMention from './ModalSupprimerMention';

interface MentionListeProps {
  mentions?: Array<Partial<MentionActe>>;
  count?: number;
  perPage?: number;
  loading?: boolean;
  onLoad?: any;
  onSearch?: any;
  onSort?: any;
  cleActe: CleMentionActe;
  identifiantActe?: number;
  /** Ouvre automatiquement la modale « Ajouter une mention » au montage. */
  autoOpenAjoutMention?: boolean;
}

const MentionListe = (props: MentionListeProps) => {
  const {
    mentions,
    count,
    perPage,
    loading,
    onLoad,
    onSearch,
    onSort,
    cleActe,
    identifiantActe,
    autoOpenAjoutMention,
  } = props;

  const { filterQuery } = React.useContext(ListeQueryContext);

  // Ouverture automatique depuis une autre page via `openMention` (ex. clic
  // sur un acte dans la modale « Saisir une mention » du dashboard) — même
  // pattern que `openSaisieEvenement` sur BlocCreation.tsx.
  const [modalAjoutOuverte, setModalAjoutOuverte] = React.useState(
    () => autoOpenAjoutMention ?? false,
  );
  const [mentionAModifier, setMentionAModifier] =
    React.useState<Partial<MentionActe> | null>(null);
  const [mentionASupprimer, setMentionASupprimer] =
    React.useState<Partial<MentionActe> | null>(null);

  // Garde les filtres après une suppression / ajout de mention dans la liste
  const reload = React.useCallback(() => filterQuery({}), [filterQuery]);

  return (
    <>
      <div className="mention-liste">
        <Liste
          title=""
          searchable
          search={{ placeholder: 'Rechercher...', clearable: true }}
          loading={loading}
          onSearch={onSearch}
        >
          <Button
            basic
            data-testid="mention-ajouter"
            onClick={() => setModalAjoutOuverte(true)}
            style={{
              color: COLORS.Background,
              borderColor: COLORS.Background,
              boxShadow: `0 0 0 1px ${COLORS.Background} inset`,
              flexShrink: 0,
            }}
          >
            <Icon name="plus" />
            Ajouter une mention
          </Button>
          <Liste.Content>
            <MentionListeTable
              items={mentions}
              count={count}
              perPage={perPage}
              loading={loading}
              onLoad={onLoad}
              onSort={onSort}
              onEdit={setMentionAModifier}
              onDelete={setMentionASupprimer}
            />
          </Liste.Content>
        </Liste>
      </div>
      <ModalAjouterMention
        open={modalAjoutOuverte}
        cleActe={cleActe}
        identifiantActe={identifiantActe}
        onClose={() => setModalAjoutOuverte(false)}
        onSaved={reload}
      />
      <ModalModifierMention
        mention={mentionAModifier}
        onClose={() => setMentionAModifier(null)}
        onSaved={reload}
      />
      <ModalSupprimerMention
        mention={mentionASupprimer}
        onClose={() => setMentionASupprimer(null)}
        onDeleted={reload}
      />
    </>
  );
};

export default MentionListe;
